import React, { useState } from "react";
import useAdminSMS from "../hooks/useAdminSMS";
import Accordion from "@mui/material/Accordion";
import {
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import useAPI from "../hooks/useAPI";
import { round } from "lodash";
import { campaignsActions } from "../state/campaigns";
import { useDispatch } from "react-redux";
import { isMobile } from "react-device-detect";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function AdminSMS(props) {
  const { currentItem, onSave, callback, canEdit, onCancelAction } = props;
  const { sendSMS } = useAdminSMS(props);
  const dispatch = useDispatch();
  const [campaignSelected, setCampaignSelected] = useState(
    currentItem?.targetAudience || "all"
  );
  const [itemSelected, setItemSelected] = useState(
    currentItem?.metadata?.campaignItemSelected || 0
  );
  const [title, setTitle] = useState(currentItem?.name || "");
  const [message, setMessage] = useState(currentItem?.description || "");
  const [id] = useState(currentItem?.id || 0);
  const [nbUsers, setNbUsers] = useState(currentItem?.metadata?.nbUsers || 0);
  const [costSMS, setCostSMS] = useState(currentItem?.metadata?.costSMS || 0);
  const [items, setItems] = useState([]);
  const [mobileTests, setMobileTests] = useState(
    currentItem?.metadata?.mobileTests || ""
  );
  const [isTestMode, setIsTestMode] = useState(
    currentItem?.metadata?.isTestMode || true
  );
  const [uploadedFile, setUploadedFile] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState(
    currentItem?.metadata?.phoneNumbers || []
  );

  const smsStats = [...(currentItem?.metadata?.stats || [])].sort(
    (a, b) => new Date(b.lastLaunch) - new Date(a.lastLaunch)
  );

  const SMS_COST = 0.02; // euro par SMS (0.056 CHF au 23.9.25)
  const SMS_COMISSION_KADOMI = 0.04; // euro par SMS (0.056 CHF au 23.9.25)

  const { data } = useAPI({
    type: "get",
    url: `sales/campaigns/items`,
    defer: false,
  });

  const { data: campaigns, query: fetchCampaigns } = useAPI({
    type: "get",
    url: `campaigns`,
    defer: true,
  });

  const { query: postItem } = useAPI({
    type: "post",
    url: `campaign`,
    defer: true,
    body: {
      id,
      type: 1,
      title:
        title !== ""
          ? title
          : `Campagne SMS ${new Date().toLocaleDateString("fr-FR")} ${new Date().toLocaleTimeString("fr-FR")}`,
      description: message,
      status: 4,
      budget: round(costSMS * 1.1, 2), // ajouter 10% pour les frais
      targetAudience: campaignSelected,
      metadata: {
        campaignItemSelected: itemSelected,
        costSMS,
        nbUsers,
        isTestMode,
        mobileTests,
        phoneNumbers: campaignSelected === "phoneList" ? phoneNumbers : [],
      },
    },
  });

  const {
    loading: loadingUserData,
    data: userData,
    query: fetchUsersPerSalesItem,
  } = useAPI({
    type: "get",
    url: `sales/campaigns/item/${itemSelected}/users?count=1`,
    defer: true,
  });

  React.useEffect(() => {
    if (campaigns && campaigns.data) {
      dispatch(campaignsActions.fetchAll(campaigns.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns]);

  React.useEffect(() => {
    if (data && data.items) {
      setItems(
        data.items.map((item) => ({
          id: item.id,
          title: item.title,
        }))
      );
    }

    async function loadUsers() {
      if (itemSelected && parseInt(itemSelected) !== 0) {
        setNbUsers(0);
        setCostSMS(0);
        try {
          await fetchUsersPerSalesItem();
        } catch (err) {
          console.error("Erreur API:", err);
        }
      }
    }
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, itemSelected]);

  // Nouveau useEffect pour surveiller userData
  React.useEffect(() => {
    if (userData && userData.users) {
      setNbUsers(userData.users);
      setCostSMS(round(userData.users * (SMS_COST + SMS_COMISSION_KADOMI), 2));
    }
  }, [userData]);

  const canSendSMS =
    message.length === 0 ||
    (campaignSelected === "typeItem" && itemSelected === null);

  const resetForm = () => {
    setCampaignSelected("all");
    setItemSelected(0);
    setMessage("");
    setNbUsers(0);
    setCostSMS(0);
    setUploadedFile(null);
    setPhoneNumbers([]);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      let numbers = [];

      // Parser pour CSV
      if (file.name.endsWith(".csv")) {
        numbers = text
          .split(/[\r\n]+/) // Séparer par les retours à la ligne
          .map((line) => line.trim()) // Supprimer les espaces au début et à la fin
          .filter((line) => line.length > 0) // Supprimer les lignes vides
          .map((num) => num.replace(/\s+/g, "")) // Supprimer tous les espaces dans le numéro
          .filter((num) => /^[\d+()-]+$/.test(num)); // Garder seulement les numéros valides
      }
      // Parser simple pour XLS/XLSX (nécessite une bibliothèque pour un parsing complet)
      else if (file.name.endsWith(".xls") || file.name.endsWith(".xlsx")) {
        // Pour XLS/XLSX, on recommande d'utiliser une bibliothèque comme 'xlsx'
        alert("Pour les fichiers Excel, veuillez convertir en CSV");
        setUploadedFile(null);
        return;
      }

      setPhoneNumbers(numbers);
      setNbUsers(numbers.length);
      setCostSMS(round(numbers.length * (SMS_COST + SMS_COMISSION_KADOMI), 2));
      setCampaignSelected("phoneList");
    };

    reader.readAsText(file);
  };

  const handleChangeItemSelected = async (event) => {
    const itemId = event.target.value;
    setItemSelected(itemId);
  };

  const handleCreateCampaign = async () => {
    try {
      await postItem();
      await fetchCampaigns();
      resetForm();
      onSave({ success: true, message: "Campagne sauvegardé avec succès" });
      callback?.();
    } catch (err) {
      console.error(
        "Erreur lors de la création de la campagne ou de l'envoi des SMS:",
        err
      );
      alert("Erreur lors de la création de la campagne ou de l'envoi des SMS.");
    }
  };

  const updateTitleWithCampaign = () => {
    const selectedItem = items.find((i) => i.id === parseInt(itemSelected));
    if (selectedItem) {
      setTitle(`Campagne "${selectedItem.title}"`);
    }
  };

  return (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid md={6} item xs={12}>
          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Envoyer un SMS</h2>
            <p>
              Veuillez remplir les informations ci-dessous pour envoyer un SMS.
            </p>

            <h3>Liste de diffusion</h3>

            <Select
              disabled={loadingUserData}
              fullWidth
              native
              value={campaignSelected}
              label="Liste de diffusion"
              onChange={(e) => {
                setCampaignSelected(e.target.value);
              }}
            >
              <option value={"all"}>Tous les utilisateurs</option>
              <option value={"typeItem"}>Campagne lié à l'article</option>
              <option value={"phoneList"}>Liste personnalisée (CSV)</option>
            </Select>

            {campaignSelected === "typeItem" ? (
              <FormControl fullWidth style={{ margin: "20px 0" }}>
                <Select
                  disabled={loadingUserData}
                  fullWidth
                  native
                  label="Type d'articles"
                  value={itemSelected}
                  onChange={handleChangeItemSelected}
                >
                  <option value={0}>Veuillez sélectionner un article</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            {campaignSelected === "phoneList" ? (
              <FormControl fullWidth style={{ margin: "20px 0" }}>
                <input
                  accept=".csv,.xls,.xlsx"
                  style={{ display: "none" }}
                  id="phone-list-file"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="phone-list-file">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    fullWidth
                  >
                    {uploadedFile
                      ? uploadedFile.name
                      : "Télécharger un fichier CSV"}
                  </Button>
                </label>
                {phoneNumbers.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
                    {phoneNumbers.length} numéro(s) de téléphone chargé(s)
                  </div>
                )}
              </FormControl>
            ) : null}

            {loadingUserData ? (
              <React.Fragment>
                <CircularProgress size={24} />
                <span style={{ marginLeft: 10 }}>
                  Calcul du nombre d'utilisateurs...
                </span>
              </React.Fragment>
            ) : null}

            <hr style={{ margin: "20px 0" }} />

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="outlined-textarea"
                label="Titre"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Entrez votre titre (Maximum 30 caractères)"
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="outlined-textarea"
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Entrez votre message (Maximum 120 caractères)"
                multiline
              />
              <div>
                <Link href="#" onClick={updateTitleWithCampaign}>
                  Utiliser comme titre
                </Link>
              </div>
            </FormControl>
          </Paper>

          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Mode simulation</h2>
            <FormControl fullWidth style={{ marginBottom: 20 }}>
              {/* checkbox de mode 'test' */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isTestMode}
                    onChange={(e) => setIsTestMode(e.target.checked)}
                    color="primary"
                  />
                }
                label="Activer le mode test (pas de frais)"
              />
              {isTestMode ? (
                <TextField
                  id="outlined-textarea"
                  label="Mode simulation (envoi un SMS à la liste ci-dessous)"
                  value={mobileTests}
                  onChange={(e) => setMobileTests(e.target.value)}
                  placeholder="Séparez les numéros de téléphone par des virgules"
                  multiline
                />
              ) : (
                <div
                  style={{
                    marginTop: 11,
                    fontStyle: "italic",
                    color: "#f32525ff",
                    backgroundColor: "#ffecec",
                    padding: 6,
                    borderRadius: 5,
                  }}
                >
                  En mode réel, les SMS seront envoyés aux utilisateurs
                </div>
              )}
            </FormControl>
          </Paper>
        </Grid>
        <Grid md={6} item xs={12}>
          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Informations</h2>
            <p>
              Cette interface permet d'envoyer des SMS à une liste
              d'utilisateurs sélectionnée. Vous pouvez choisir d'envoyer le
              message à tous les utilisateurs, à ceux qui ont loué un type
              d'article spécifique, ou à ceux situés dans une zone géographique
              particulière.
            </p>
            <p>
              Assurez-vous que le message ne dépasse pas 120 caractères pour
              garantir une bonne réception. Utilisez cette fonctionnalité avec
              parcimonie pour éviter de submerger les utilisateurs avec trop de
              messages.
            </p>
          </Paper>
          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Statistiques</h2>
            <h4>
              Compte tenu du nombre d'utilisateurs pouvant évoluer, le coût
              estimé peut varier par rapport aux données de la liste initiale.
            </h4>
            <p>Nombre d'utilisateurs dans la liste sélectionnée : {nbUsers}</p>
            <p>Coût estimé pour l'envoi des SMS : {costSMS} CHF</p>

            {phoneNumbers.length > 0 ? (
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  {/* bold */}
                  <Typography component="span" style={{ fontWeight: "bold" }}>
                    Détails
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <table
                    style={{
                      width: "200px",
                      borderCollapse: "collapse",
                      fontSize: 11,
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            border: "1px solid #ddd",
                            padding: 8,
                            textAlign: "left",
                          }}
                        >
                          Numéro de téléphone
                        </th>
                      </tr>
                    </thead>
                    <tbody
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        display: "block",
                      }}
                    >
                      {phoneNumbers.map((number, index) => (
                        <tr key={index}>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: 8,
                              width: "200px",
                            }}
                          >
                            {number.trim()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </AccordionDetails>
              </Accordion>
            ) : null}
          </Paper>
          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Historiques envois</h2>
            <h4>Vous trouverez ci-dessous les historiques d'envoi :</h4>

            {/* create a table with column : date, nombre d'envoi */}
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 11,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: 8,
                      textAlign: "left",
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: 8,
                      textAlign: "left",
                    }}
                  >
                    Nombre d'envois
                  </th>
                  {!isMobile && (
                    <th
                      style={{
                        border: "1px solid #ddd",
                        padding: 8,
                        textAlign: "left",
                      }}
                    >
                      Batch ID
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {smsStats.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        border: "1px solid #ddd",
                        padding: 8,
                        textAlign: "center",
                      }}
                    >
                      Aucune donnée disponible
                    </td>
                  </tr>
                ) : (
                  (smsStats || []).map((entry, index) => (
                    <tr key={index}>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: 8,
                        }}
                      >
                        {/* date + heure */}
                        {new Date(entry.lastLaunch).toLocaleDateString("fr-FR")}
                        <span
                          style={{
                            color: "#282828ff",
                            fontSize: 12,
                            marginLeft: 5,
                          }}
                        >
                          {new Date(entry.lastLaunch).toLocaleTimeString(
                            "fr-FR"
                          )}
                        </span>
                      </td>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: 8,
                        }}
                      >
                        {entry.sent}{" "}
                      </td>
                      {!isMobile && (
                        <td
                          style={{
                            border: "1px solid #ddd",
                            padding: 8,
                          }}
                        >
                          {entry.batchId}{" "}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Paper>
        </Grid>
      </Grid>

      <div style={{ height: 50 }}>
        <Button
          variant="contained"
          disabled={canSendSMS || loadingUserData}
          onClick={handleCreateCampaign}
        >
          {id ? "Modifier" : "Créer"}
        </Button>
        <Button
          disabled={loadingUserData}
          variant="contained"
          style={{ marginLeft: 10 }}
          onClick={resetForm}
        >
          Réinitialiser
        </Button>
        <Button
          disabled={loadingUserData}
          style={{ marginLeft: 10 }}
          variant="contained"
          onClick={onCancelAction}
        >
          Annuler
        </Button>
      </div>
    </React.Fragment>
  );
}
